import React from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  Pressable,
  useWindowDimensions,
  Linking,
} from 'react-native';
import {
  DrawerContentComponentProps,
  DrawerContentScrollView,
} from '@react-navigation/drawer';
import { Avatar, Text, useTheme, Divider, List } from 'react-native-paper';
import {
  Home,
  BookOpen,
  Users,
  LogOut,
  Map,
  Brain,
  Settings,
  Coffee,
  Lightbulb,
  FileText,
  HelpCircle,
  User,
} from 'lucide-react-native';
import { useLogout } from '../../hooks/useLogout';
import { useMenuNavigation } from '../../hooks/useMenuNavigation';

export function DrawerContent(props: DrawerContentComponentProps) {
  const theme = useTheme();
  const { navigation } = props;
  const windowDimensions = useWindowDimensions();
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);
  const { logout } = useLogout();
  const {
    navigateToTab,
    navigateToDrawer,
    navigateToSettings,
    navigateToRecommendations,
    navigateToTermsAndConditions,
    navigateToHelpSupport,
  } = useMenuNavigation(navigation);

  React.useEffect(() => {
    const updateScreenSize = () => {
      const { width, height } = Dimensions.get('window');
      const minDimension = Math.min(width, height);
      setIsSmallScreen(minDimension < 768);
    };

    updateScreenSize();
    const sub = Dimensions.addEventListener('change', updateScreenSize);
    return () => sub?.remove();
  }, []);

  // Responsive styles for logout button
  const isSmallDevice = windowDimensions.width < 375;
  const logoutButtonStyle = {
    paddingHorizontal: isSmallDevice ? 12 : 16,
    paddingVertical: isSmallDevice ? 10 : 12,
  };

  // Navigation functions are now provided by useMenuNavigation hook

  return (
    <DrawerContentScrollView
      {...props}
      contentContainerStyle={[
        styles.container,
        { backgroundColor: theme.colors.surface },
      ]}
    >
      <View style={styles.header}>
        <Avatar.Text
          size={56}
          label='CP'
          style={{ backgroundColor: theme.colors.primary }}
        />
        <View style={styles.headerText}>
          <Text variant='titleMedium' style={{ color: theme.colors.onSurface }}>
            CoffeePlexity
          </Text>
          <Text
            variant='bodySmall'
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            Brew better, every day
          </Text>
        </View>
      </View>

      <Divider />

      {/* Main Tab Routes - Only show on large screens */}
      {!isSmallScreen && (
        <View style={styles.section}>
          <Text
            variant='labelSmall'
            style={[
              styles.sectionLabel,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            MAIN NAVIGATION
          </Text>
          <List.Item
            title='Recipes'
            onPress={() => navigateToTab('recipes')}
            left={props => (
              <BookOpen
                {...props}
                size={20}
                color={theme.colors.onSurfaceVariant}
              />
            )}
            titleStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title='Creators'
            onPress={() => navigateToTab('creators')}
            left={props => (
              <Users
                {...props}
                size={20}
                color={theme.colors.onSurfaceVariant}
              />
            )}
            titleStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title='Equipment'
            onPress={() => navigateToTab('equipment')}
            left={props => (
              <Coffee
                {...props}
                size={20}
                color={theme.colors.onSurfaceVariant}
              />
            )}
            titleStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title='Profile'
            onPress={() => navigateToTab('profile')}
            left={props => (
              <User
                {...props}
                size={20}
                color={theme.colors.onSurfaceVariant}
              />
            )}
            titleStyle={{ color: theme.colors.onSurface }}
          />
        </View>
      )}

      {!isSmallScreen && <Divider style={{ marginVertical: 8 }} />}

      {/* Drawer/Hamburger Menu Items */}
      <View style={styles.section}>
        <Text
          variant='labelSmall'
          style={[
            styles.sectionLabel,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          DISCOVER
        </Text>
        <List.Item
          title='Recommendations'
          onPress={() => navigateToRecommendations()}
          left={props => (
            <Lightbulb
              {...props}
              size={20}
              color={theme.colors.onSurfaceVariant}
            />
          )}
          titleStyle={{ color: theme.colors.onSurface }}
        />
        <List.Item
          title='Journey'
          onPress={() => navigateToDrawer('journey')}
          left={props => (
            <Map {...props} size={20} color={theme.colors.onSurfaceVariant} />
          )}
          titleStyle={{ color: theme.colors.onSurface }}
        />
        <List.Item
          title='AI Experiments (Alpha)'
          onPress={() =>
            Linking.openURL('https://ai-assistant.coffeeplexity.com/')
          }
          left={props => (
            <Brain {...props} size={20} color={theme.colors.onSurfaceVariant} />
          )}
          titleStyle={{ color: theme.colors.onSurface }}
        />
      </View>

      <Divider style={{ marginVertical: 8 }} />

      {/* Account & Support Section */}
      <View style={styles.section}>
        <Text
          variant='labelSmall'
          style={[
            styles.sectionLabel,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          ACCOUNT & SUPPORT
        </Text>
        <List.Item
          title='Settings'
          onPress={() => navigateToSettings()}
          left={props => (
            <Settings
              {...props}
              size={20}
              color={theme.colors.onSurfaceVariant}
            />
          )}
          titleStyle={{ color: theme.colors.onSurface }}
        />
        <List.Item
          title='Terms & Conditions'
          onPress={() => navigateToTermsAndConditions()}
          left={props => (
            <FileText
              {...props}
              size={20}
              color={theme.colors.onSurfaceVariant}
            />
          )}
          titleStyle={{ color: theme.colors.onSurface }}
        />
        <List.Item
          title='Help & Support'
          onPress={() => navigateToHelpSupport()}
          left={props => (
            <HelpCircle
              {...props}
              size={20}
              color={theme.colors.onSurfaceVariant}
            />
          )}
          titleStyle={{ color: theme.colors.onSurface }}
        />
      </View>

      <View style={styles.footer}>
        <Pressable
          onPress={() => {
            logout(true);
          }}
          style={({ pressed }) => [
            styles.logoutButton,
            logoutButtonStyle,
            {
              borderColor: theme.colors.error,
              opacity: pressed ? 0.7 : 1,
            },
          ]}
          accessibilityRole='button'
          accessibilityLabel='Logout from your account'
          testID='drawer-logout-button'
        >
          <LogOut size={18} color={theme.colors.error} />
          <Text
            style={[styles.logoutButtonText, { color: theme.colors.error }]}
            variant={isSmallDevice ? 'bodySmall' : 'bodyMedium'}
          >
            Logout
          </Text>
        </Pressable>
        <Text
          variant='bodySmall'
          style={[styles.versionText, { color: theme.colors.onSurfaceVariant }]}
        >
          v1.0.0
        </Text>
      </View>
    </DrawerContentScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
  },
  headerText: { flex: 1 },
  section: { paddingVertical: 8 },
  sectionLabel: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  footer: {
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#e0e0e0',
    marginTop: 'auto',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  logoutButtonText: {
    fontWeight: '600',
  },
  versionText: {
    marginTop: 12,
    textAlign: 'center',
  },
});

export default DrawerContent;
